import { Dispatch } from "react";
import JSZip from "jszip";
import { FragmentsGroup } from "@thatopen/fragments";
import * as OBC from "@thatopen/components";
import * as OBF from "@thatopen/components-front";
import * as CUI from "@thatopen/ui-obc";
import * as WEBIFC from "web-ifc";
import * as THREE from "three";
import { Base64ToUint8Array, FileToBase64 } from "../../helper/general/methods";

export class StandardScene {
  private components: OBC.Components;
  private worlds: OBC.Worlds;
  private scene: THREE.Scene;
  private fragments: OBC.FragmentsManager;
  private ifcLoader: OBC.IfcLoader;
  private highlighter: OBF.Highlighter;
  private hider: OBC.Hider;
  private indexer?: OBC.IfcRelationsIndexer;
  private dispatch: Dispatch<any>;
  public zipModelFragments: any;

  constructor(container: HTMLDivElement, dispatch: Dispatch<any>) {
    this.dispatch = dispatch;
    this.components = new OBC.Components();
    this.worlds = this.components.get(OBC.Worlds);

    const world = this.worlds.create<
      OBC.SimpleScene,
      OBC.OrthoPerspectiveCamera,
      OBF.PostproductionRenderer
    >();

    const sceneComponent = new OBC.SimpleScene(this.components);
    world.scene = sceneComponent;
    this.scene = world.scene.three;
    sceneComponent.setup();
    this.scene.background = null;

    // const grids = this.components.get(OBC.Grids);
    // grids.create(world);

    const rendererComponent = new OBF.PostproductionRenderer(
      this.components,
      container
    );
    world.renderer = rendererComponent;

    const orthoCamera = new OBC.OrthoPerspectiveCamera(this.components);
    world.camera = orthoCamera;

    this.components.init();

    rendererComponent.postproduction.enabled = true;

    this.fragments = this.components.get(OBC.FragmentsManager);

    this.highlighter = this.components.get(OBF.Highlighter);
    this.highlighter.setup({ world });
    this.highlighter.zoomToSelection = true;
    this.highlighter.events.select.onHighlight.add((selection) => {
      // this.dispatch({
      //   type: "RESPONSE",
      //   response: "OnHighlight",
      // });
    });
    this.highlighter.events.select.onClear.add(() => {
      // this.dispatch({
      //   type: "RESPONSE",
      //   response: "OnClear",
      // });
    });

    this.hider = this.components.get(OBC.Hider);

    this.ifcLoader = this.components.get(OBC.IfcLoader);

    this.fragments.onFragmentsLoaded.add(async (model: FragmentsGroup) => {
      this.indexer = this.components.get(OBC.IfcRelationsIndexer);
    });
  }

  Dispose() {
    if (this.components) {
      this.components.dispose();
    }
    (this.components as any) = undefined;
    if (this.fragments) {
      this.fragments.dispose();
      (this.fragments as any) = undefined;
    }
  }

  HideModelElement() {
    const selection = this.highlighter.selection.select;
    if (Object.keys(selection).length === 0) return;
    this.hider.set(false, selection);
  }

  IsolateModelElement() {
    const selection = this.highlighter.selection.select;
    if (Object.keys(selection).length === 0) return;
    this.hider.isolate(selection);
  }

  UnhideAllModelElements() {
    this.hider.set(true);
  }

  async ShowModelElementProperties() {
    if (!this.indexer) return;
    const selection = this.highlighter.selection.select;
    for (const fragmentID in selection) {
      const fragment = this.fragments.list.get(fragmentID);
      const fragmentModel = fragment?.group;
      if (!fragmentModel) continue;
      this.indexer.process(fragmentModel);
      const ids = selection[fragmentID];
      const expressIDs = Array.from(ids);
      for (const expressID of expressIDs) {
        const properties = this.indexer.getEntityRelations(
          fragmentModel,
          expressID,
          "IsDefinedBy"
        );
        if (properties) {
          for (const propertyExpressID of properties) {
            const property = await fragmentModel.getProperties(
              propertyExpressID
            );
            console.log(property);
          }
        }
      }
      this.indexer.dispose();
    }
  }

  async ConvertIfcToFragments(ifcModel: any) {
    try {
      await this.ifcLoader.setup();

      const uint8Array = Base64ToUint8Array(ifcModel["modelFileData"]);
      // model: Promise <FragmentsGroup>
      const model = await this.ifcLoader.load(
        uint8Array,
        true,
        ifcModel["modelName"]
      );
      this.scene.add(model);

      return await this.CreateZipModelFragments(
        ifcModel["modelName"],
        this.fragments
      );
    } catch (error: any) {
      // Handle the error, e.g., log or perform other error-handling actions
      console.error(`An error occurred: ${error.message}`);
    }
  }

  async CreateZipModelFragments(
    modelName: string,
    modelFragments: OBC.FragmentsManager
  ) {
    if (!modelFragments.groups.size) {
      return;
    }
    const groupData = modelFragments.groups.entries().next().value;

    if (groupData) {
      const [groupKey, group] = groupData;
      const data = modelFragments.export(group);

      // Create a new JSZip instance
      const zip = new JSZip();
      // Add the fragment file
      zip.file("model.frag", data);
      // Add the model.json file
      zip.file("model.json", JSON.stringify(group.getLocalProperties()));
      // Generate the zip file
      const zipFile = await zip.generateAsync({ type: "blob" });
      const modelSize = zipFile.size;
      // Convert the zip file to base64
      const modelData = await FileToBase64(zipFile);
      const result = {
        modelName: modelName,
        modelFragmentsData: modelData,
        modelFragmentsSize: modelSize,
      };
      return result;
    } else {
      console.log("No group data");
      return;
    }
  }

  async OpenModelFragments(zipModel: {
    modelFragmentsData: string;
  }): Promise<boolean> {
    try {
      const files = await this.UnzipModelFragments(zipModel.modelFragmentsData);

      // Find the fragment and properties files
      const fragFile = files.find((file) =>
        file.relativePath.endsWith(".frag")
      );
      const propsFile = files.find((file) =>
        file.relativePath.endsWith(".json")
      );

      if (!fragFile || !propsFile) {
        throw new Error("Required files not found in zip");
      }

      // Load the fragment model
      const fragContent = await fragFile.content;
      if (!(fragContent instanceof ArrayBuffer)) {
        throw new Error("Invalid fragment file content");
      }

      const buffer = new Uint8Array(fragContent);
      const model = this.fragments.load(buffer);
      console.log(model);
      console.log(model.hasProperties);
      // Load and set properties
      const propsContent = await propsFile.content;
      const properties = new TextDecoder().decode(propsContent);
      model.setLocalProperties(JSON.parse(properties));

      // Add model to scene
      this.scene.add(model);
      return true;
    } catch (error) {
      console.error("Error opening model fragments:", error);
      return false;
    }
  }

  async UnzipModelFragments(zipModelData: string): Promise<any[]> {
    try {
      const zip = await JSZip.loadAsync(zipModelData, { base64: true });
      const files: any[] = [];

      zip.forEach((relativePath, file) => {
        files.push({ relativePath, content: file.async("arraybuffer") });
      });
      return files;
    } catch (error) {
      console.error("Error in UnzipModelFragments:", error);
      throw error;
    }
  }
}
